import type { StaffRole } from "@rekati/api";

/**
 * Which role opens which area: the backend's matrix (docs/api.md 38.1, 42,
 * 44, 45) as the console's map. It routes and hides; it never secures. The
 * API answers 403 to a role that does not fit whatever the UI shows.
 */
export type AreaId =
  | "scan"
  | "lookup"
  | "walk-in"
  | "board"
  | "overview"
  | "attendees"
  | "sessions"
  | "demographics"
  | "users"
  | "event"
  | "activities"
  | "content"
  | "email"
  | "survey";

export type AreaGroup = "door" | "live" | "numbers" | "manage";

export interface Area {
  id: AreaId;
  path: string;
  group: AreaGroup;
  roles: readonly StaffRole[];
  /** The board is a television: no rail, no assistant. */
  chromeless?: boolean;
}

const DOOR = ["frontdesk", "admin", "superuser"] as const;
const BOARD = ["monitor", "admin", "superuser"] as const;
const ADMIN = ["admin", "superuser"] as const;
const SUPER = ["superuser"] as const;

export const AREAS: readonly Area[] = [
  { id: "scan", path: "/scan", group: "door", roles: DOOR },
  { id: "lookup", path: "/lookup", group: "door", roles: DOOR },
  { id: "walk-in", path: "/walk-in", group: "door", roles: DOOR },
  { id: "board", path: "/board", group: "live", roles: BOARD, chromeless: true },
  { id: "overview", path: "/overview", group: "numbers", roles: BOARD },
  // Frontdesk too (the owner, 28 October 2026): they find somebody without their QR here and check
  // them in from the record. The export and the delete stay admin only, on the API as well.
  { id: "attendees", path: "/attendees", group: "numbers", roles: DOOR },
  // 42.3 is open to frontdesk too (decision 93). The Sessions page is hidden for now: no role opens
  // it, so it leaves the rail and its route sends everyone home. Restore ADMIN here to bring it back.
  { id: "sessions", path: "/sessions", group: "numbers", roles: [] },
  // "Who is coming" is hidden for now: no role opens it, so it leaves the rail and its route sends
  // everyone home. Restore ADMIN here to bring it back.
  { id: "demographics", path: "/demographics", group: "numbers", roles: [] },
  { id: "users", path: "/users", group: "manage", roles: SUPER },
  { id: "event", path: "/event", group: "manage", roles: SUPER },
  { id: "activities", path: "/activities", group: "manage", roles: SUPER },
  { id: "content", path: "/content", group: "manage", roles: SUPER },
  { id: "email", path: "/email", group: "manage", roles: SUPER },
  // The survey's questions are the admin's to edit too (the owner, 27 October 2026).
  { id: "survey", path: "/survey", group: "manage", roles: ADMIN },
];

/** Where each role lands after sign-in. */
export const HOME: Record<StaffRole, string> = {
  frontdesk: "/scan",
  monitor: "/board",
  admin: "/overview",
  superuser: "/overview",
};

export function canOpen(role: StaffRole, area: Area): boolean {
  return area.roles.includes(role);
}

export function areasFor(role: StaffRole): Area[] {
  return AREAS.filter((a) => canOpen(role, a));
}

/** Kara is admin and superuser only (44). */
export function hasAssistant(role: StaffRole): boolean {
  return role === "admin" || role === "superuser";
}
